import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CaretDown, CheckCircle, Coffee, FilePdf, MagicWand, PencilSimple, Plus, Printer, Trash, WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatErr } from "@/lib/api";

export const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
export const PERIODS = [1, 2, 3, 4, 5, 6, 7];

export function WeekGrid({ slots, breaks = [], onCellClick, cellLabel, testidPrefix = "cell" }) {
  const { t } = useTranslation();
  const dayLabels = [
    t("timetable.days.monday", { defaultValue: "Monday" }),
    t("timetable.days.tuesday", { defaultValue: "Tuesday" }),
    t("timetable.days.wednesday", { defaultValue: "Wednesday" }),
    t("timetable.days.thursday", { defaultValue: "Thursday" }),
    t("timetable.days.friday", { defaultValue: "Friday" }),
  ];
  const byKey = useMemo(() => {
    const m = {};
    slots.forEach((s) => {
      const k = `${s.day_of_week}-${s.period_number}`;
      m[k] = m[k] ? [...m[k], s] : [s];
    });
    return m;
  }, [slots]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm min-w-[680px]">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left text-xs font-bold text-slate-400 border border-slate-100 bg-[#F4F5F7] w-16">{t("timetable.period", { defaultValue: "Period" })}</th>
            {DAY_NAMES.map((d, i) => (
              <th key={d} className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wide border border-slate-100 bg-[#F4F5F7]">{dayLabels[i]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PERIODS.map((p) => (
            <Fragment key={p}>
            <tr>
              <td className="px-3 py-2 text-xs font-bold text-slate-400 border border-slate-100">P{p}</td>
              {DAY_NAMES.map((_, d) => {
                const cellSlots = byKey[`${d}-${p}`] || [];
                return (
                  <td
                    key={d}
                    data-testid={`${testidPrefix}-${d}-${p}`}
                    className={`border border-slate-100 p-1.5 align-top ${onCellClick ? "cursor-pointer" : ""}`}
                    onClick={() => onCellClick && cellSlots.length === 0 && onCellClick(d, p, null)}
                  >
                    {cellSlots.length > 0 ? (
                      <div className="space-y-1">
                        {cellSlots.map((s) => (
                          <div
                            key={s.id}
                            onClick={(e) => {
                              if (!onCellClick) return;
                              e.stopPropagation();
                              onCellClick(d, p, s);
                            }}
                            className="rounded-lg bg-brand-coral/10 px-2 py-1.5 hover:bg-brand-coral/15 transition-colors duration-200"
                          >
                            <p className="text-xs font-bold text-brand-coralDark">{s.subject_name}{cellSlots.length > 1 && s.class_name ? ` · ${s.class_name}` : ""}</p>
                            <p className="text-[10px] text-slate-500">{cellLabel ? cellLabel(s) : `${s.teacher_name} · ${s.room_name}`}</p>
                          </div>
                        ))}
                      </div>
                    ) : onCellClick ? (
                      <div className="rounded-lg border border-dashed border-slate-200 px-2 py-1.5 text-center text-[10px] text-slate-300 hover:border-brand-coral hover:text-brand-coral transition-colors duration-200">+</div>
                    ) : (
                      <div className="px-2 py-1.5 text-[10px] text-slate-200">—</div>
                    )}
                  </td>
                );
              })}
            </tr>
            {breaks.filter((b) => b.after_period === p).map((b) => (
              <tr key={`break-${p}-${b.name}`} data-testid={`break-row-after-p${p}`}>
                <td className="px-3 py-1.5 border border-slate-100 text-center">
                  <Coffee size={13} weight="duotone" className="text-amber-500 inline" />
                </td>
                <td colSpan={DAY_NAMES.length} className="border border-slate-100 px-3 py-1.5 bg-status-amber/10">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600">{b.name}</span>
                </td>
              </tr>
            ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SlotModal({ modal, classes, teachers, rooms, subjects, defaultClassId, onClose, onSaved }) {
  const isEdit = !!modal.slot;
  const [form, setForm] = useState(() =>
    modal.slot
      ? { class_id: modal.slot.class_id, subject_id: modal.slot.subject_id, teacher_id: modal.slot.teacher_id, room_id: modal.slot.room_id }
      : { class_id: defaultClassId || "", subject_id: "", teacher_id: "", room_id: "" }
  );
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.class_id || !form.subject_id || !form.teacher_id || !form.room_id) {
      setErr("Select class, subject, teacher and room");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form, day_of_week: modal.day, period_number: modal.period };
      if (isEdit) await api.patch(`/school/timetable/slots/${modal.slot.id}`, payload);
      else await api.post("/school/timetable/slots", payload);
      toast.success(isEdit ? "Slot updated" : "Slot added");
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/school/timetable/slots/${modal.slot.id}`);
      toast.success("Slot removed");
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatErr(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit" : "Add"} slot — {DAY_NAMES[modal.day]}, Period {modal.period}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="slot-modal">
          {err && <p className="text-xs font-semibold text-status-red bg-status-red/10 rounded-xl px-4 py-2.5" data-testid="slot-conflict-error">{err}</p>}
          {[
            ["Class", "class_id", classes.map((c) => [c.id, c.name]), "slot-class-select"],
            ["Subject", "subject_id", subjects.map((s) => [s.id, s.subject_name]), "slot-subject-select"],
            ["Teacher", "teacher_id", teachers.map((t) => [t.id, t.name]), "slot-teacher-select"],
            ["Room", "room_id", rooms.map((r) => [r.id, `${r.room_name} (${r.room_type})`]), "slot-room-select"],
          ].map(([label, key, options, testid]) => (
            <div key={key}>
              <Label>{label}</Label>
              <Select value={form[key]} onValueChange={(v) => setForm({ ...form, [key]: v })}>
                <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
                <SelectContent>
                  {options.map(([id, name]) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          <div className="flex gap-3">
            {isEdit && (
              <Button type="button" variant="outline" data-testid="slot-delete-btn" onClick={remove} className="rounded-full font-bold border-status-red/40 text-status-red hover:bg-status-red/10 transition-transform duration-300 active:scale-95">
                <Trash size={15} className="mr-1.5" /> Remove
              </Button>
            )}
            <Button data-testid="slot-save-btn" onClick={save} disabled={saving} className="flex-1 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
              {saving ? "Saving…" : "Save slot"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConstraintEditor({ teacher, onClose, onSaved }) {
  const [department, setDepartment] = useState(teacher.department || "");
  const [maxHours, setMaxHours] = useState(teacher.max_weekly_hours ?? "");
  const [unavail, setUnavail] = useState(() => new Set((teacher.unavailable_slots || []).map((u) => `${u.day}-${u.period}`)));
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    const next = new Set(unavail);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setUnavail(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/school/teachers/${teacher.id}/constraints`, {
        department,
        max_weekly_hours: maxHours === "" ? null : Number(maxHours),
        unavailable_slots: [...unavail].map((k) => {
          const [d, p] = k.split("-");
          return { day: Number(d), period: Number(p) };
        }),
      });
      toast.success("Constraints saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Scheduling constraints — {teacher.name}</DialogTitle></DialogHeader>
        <div className="space-y-4" data-testid="constraint-editor">
          <div className="grid grid-cols-2 gap-4">
            <div><Label htmlFor="constraint-dept">Department</Label><Input id="constraint-dept" data-testid="constraint-department" className="mt-1.5" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Mathematics" /></div>
            <div><Label htmlFor="constraint-max">Max weekly hours</Label><Input id="constraint-max" data-testid="constraint-max-hours" type="number" min="1" max="35" className="mt-1.5" value={maxHours} onChange={(e) => setMaxHours(e.target.value)} placeholder="e.g. 25" /></div>
          </div>
          <div>
            <Label>Unavailable slots (tap to mark)</Label>
            <div className="mt-2 overflow-x-auto">
              <table className="border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="p-1" />
                    {DAY_NAMES.map((d) => <th key={d} className="p-1 font-bold text-slate-500">{d.slice(0, 3)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {PERIODS.map((p) => (
                    <tr key={p}>
                      <td className="p-1 font-bold text-slate-400">P{p}</td>
                      {DAY_NAMES.map((_, d) => {
                        const key = `${d}-${p}`;
                        const active = unavail.has(key);
                        return (
                          <td key={d} className="p-0.5">
                            <button
                              type="button"
                              data-testid={`unavail-${d}-${p}`}
                              onClick={() => toggle(key)}
                              className={`w-9 h-7 rounded-md text-[10px] font-bold transition-colors duration-150 ${active ? "bg-status-red text-white" : "bg-slate-100 text-slate-400 hover:bg-status-red/20"}`}
                            >
                              {active ? "✕" : ""}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Button data-testid="constraint-save-btn" onClick={save} disabled={saving} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">
            {saving ? "Saving…" : "Save constraints"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TimetableTab() {
  const [view, setView] = useState("grid");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [slots, setSlots] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [filter, setFilter] = useState({ class_id: "", teacher_ids: [], room_id: "" });
  const [breaks, setBreaks] = useState([]);
  const [breakForm, setBreakForm] = useState({ name: "", after_period: "2" });
  const [modal, setModal] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [roomForm, setRoomForm] = useState({ room_name: "", capacity: "30", room_type: "standard" });
  const [subjectForm, setSubjectForm] = useState({ subject_name: "", weekly_required_periods: "3" });

  const loadMeta = async () => {
    const [c, t, r, sub] = await Promise.all([
      api.get("/school/classes"),
      api.get("/school/teachers"),
      api.get("/school/rooms"),
      api.get("/school/subjects"),
    ]);
    setClasses(c.data);
    setTeachers(t.data);
    setRooms(r.data);
    setSubjects(sub.data);
  };
  const loadSlots = async () => {
    const params = {};
    if (filter.class_id) params.class_id = filter.class_id;
    if (filter.teacher_ids.length) params.teacher_ids = filter.teacher_ids.join(",");
    if (filter.room_id) params.room_id = filter.room_id;
    const { data } = await api.get("/school/timetable", { params });
    setSlots(data);
  };
  const loadBreaks = async () => setBreaks((await api.get("/timetable/breaks")).data);
  const loadConflicts = async () => setConflicts((await api.get("/school/timetable/conflicts")).data);
  const reloadAll = async () => {
    try {
      await Promise.all([loadSlots(), loadConflicts(), loadBreaks()]);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadMeta();
        await reloadAll();
      } catch (e) {
        toast.error(formatErr(e));
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSlots().catch((e) => toast.error(formatErr(e)));
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoGenerate = async () => {
    if (!filter.class_id) {
      toast.error("Pick a class in the filter first");
      return;
    }
    setGenBusy(true);
    try {
      const { data } = await api.post("/school/timetable/auto-generate", { class_id: filter.class_id, replace: true });
      toast.success(`Generated ${data.placed} slots` + (data.unplaced.length ? ` · could not place: ${data.unplaced.join(", ")}` : ""));
      reloadAll();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setGenBusy(false);
    }
  };

  const addRoom = async (e) => {
    e.preventDefault();
    try {
      await api.post("/school/rooms", { ...roomForm, capacity: Number(roomForm.capacity) || 30 });
      toast.success("Room added");
      setRoomForm({ room_name: "", capacity: "30", room_type: "standard" });
      loadMeta();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const addSubject = async (e) => {
    e.preventDefault();
    try {
      await api.post("/school/subjects", { ...subjectForm, weekly_required_periods: Number(subjectForm.weekly_required_periods) || 1 });
      toast.success("Subject added");
      setSubjectForm({ subject_name: "", weekly_required_periods: "3" });
      loadMeta();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const deleteRoom = async (id) => {
    try {
      await api.delete(`/school/rooms/${id}`);
      toast.success("Room removed");
      loadMeta();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const deleteSubject = async (id) => {
    try {
      await api.delete(`/school/subjects/${id}`);
      toast.success("Subject removed");
      loadMeta();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const toggleTeacherFilter = (id, on) =>
    setFilter((f) => ({ ...f, teacher_ids: on ? [...f.teacher_ids, id] : f.teacher_ids.filter((t) => t !== id) }));

  const saveBreaks = async (next) => {
    try {
      const { data } = await api.put("/school/timetable/breaks", next);
      setBreaks(data);
      toast.success("Breaks updated");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const addBreak = (e) => {
    e.preventDefault();
    saveBreaks([...breaks, { name: breakForm.name, after_period: Number(breakForm.after_period) }]);
    setBreakForm({ name: "", after_period: "2" });
  };

  const removeBreak = (idx) => saveBreaks(breaks.filter((_, i) => i !== idx));

  const clearTimetable = async () => {
    try {
      const { data } = await api.delete("/school/timetable", { params: filter.class_id ? { class_id: filter.class_id } : {} });
      toast.success(`Cleared ${data.deleted} slot${data.deleted === 1 ? "" : "s"} — ready to rebuild`);
      setConfirmClear(false);
      reloadAll();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const downloadPdf = async () => {
    try {
      const params = { format: "pdf" };
      if (filter.class_id) params.class_id = filter.class_id;
      if (filter.teacher_ids.length) params.teacher_ids = filter.teacher_ids.join(",");
      if (filter.room_id) params.room_id = filter.room_id;
      const res = await api.get("/school/timetable/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "timetable.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    }
  };

  const printGrid = () => {
    const q = new URLSearchParams();
    if (filter.class_id) q.set("class_id", filter.class_id);
    if (filter.teacher_ids.length) q.set("teacher_ids", filter.teacher_ids.join(","));
    if (filter.room_id) q.set("room_id", filter.room_id);
    window.open(`/print/timetable?${q.toString()}`, "_blank");
  };

  const subTabs = [
    ["grid", "Weekly grid"],
    ["setup", "Rooms & subjects"],
    ["constraints", "Teacher constraints"],
    ["conflicts", `Conflicts${conflicts.length ? ` (${conflicts.length})` : ""}`],
  ];

  return (
    <div data-testid="timetable-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-extrabold">Timetable</h1>
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {subTabs.map(([id, label]) => (
            <button
              key={id}
              data-testid={`timetable-subtab-${id}`}
              onClick={() => setView(id)}
              className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors duration-200 ${view === id ? "bg-brand-coral text-white" : "bg-white border border-slate-200 text-slate-600"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "grid" && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Select value={filter.class_id || "all"} onValueChange={(v) => setFilter({ ...filter, class_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="timetable-filter-class" className="w-44 bg-white"><SelectValue placeholder="All classes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="timetable-filter-teacher" className="w-52 justify-between bg-white font-semibold">
                  {filter.teacher_ids.length === 0 ? "All teachers" : `${filter.teacher_ids.length} teacher${filter.teacher_ids.length > 1 ? "s" : ""} selected`}
                  <CaretDown size={14} className="text-slate-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 rounded-2xl p-2" align="start">
                {filter.teacher_ids.length > 0 && (
                  <button data-testid="teacher-filter-clear" onClick={() => setFilter({ ...filter, teacher_ids: [] })} className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-brand-coralDark hover:bg-brand-coral/10 transition-colors duration-150">
                    Clear — show all teachers
                  </button>
                )}
                {teachers.map((t) => (
                  <label key={t.id} data-testid={`teacher-filter-option-${t.id}`} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm font-medium">
                    <Checkbox checked={filter.teacher_ids.includes(t.id)} onCheckedChange={(c) => toggleTeacherFilter(t.id, !!c)} />
                    {t.name}
                  </label>
                ))}
              </PopoverContent>
            </Popover>
            <Select value={filter.room_id || "all"} onValueChange={(v) => setFilter({ ...filter, room_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="timetable-filter-room" className="w-44 bg-white"><SelectValue placeholder="All rooms" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rooms</SelectItem>
                {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.room_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <Button variant="outline" data-testid="timetable-print-btn" onClick={printGrid} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
              <Printer size={15} className="mr-1.5" /> Print
            </Button>
            <Button variant="outline" data-testid="timetable-pdf-btn" onClick={downloadPdf} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
              <FilePdf size={15} className="mr-1.5" /> PDF
            </Button>
            <Button variant="outline" data-testid="clear-timetable-btn" onClick={() => setConfirmClear(true)} className="rounded-full font-bold border-status-red/40 text-status-red hover:bg-status-red/10 transition-transform duration-300 active:scale-95">
              <Trash size={15} className="mr-1.5" /> {filter.class_id ? "Clear class" : "Clear all"}
            </Button>
            <Button data-testid="auto-generate-btn" onClick={autoGenerate} disabled={genBusy} className="rounded-full bg-gradient-to-r from-brand-yellow to-brand-coral text-white font-bold border-0 transition-transform duration-300 active:scale-95">
              <MagicWand size={16} className="mr-1.5" /> {genBusy ? "Generating…" : "Auto-generate"}
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="timetable-grid">
            <WeekGrid slots={slots} breaks={breaks} onCellClick={(d, p, s) => setModal({ day: d, period: p, slot: s || null })} testidPrefix="slot-cell" />
          </div>
          <p className="text-xs text-slate-400 mt-3">Click an empty cell to assign a slot, or a filled cell to edit/remove it. Conflicts are blocked automatically.</p>
        </div>
      )}

      {view === "setup" && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-heading font-bold mb-4">Rooms</h2>
            <form onSubmit={addRoom} className="grid grid-cols-3 gap-3 mb-4" data-testid="room-form">
              <Input data-testid="room-name-input" required placeholder="Room name" value={roomForm.room_name} onChange={(e) => setRoomForm({ ...roomForm, room_name: e.target.value })} />
              <Input data-testid="room-capacity-input" type="number" min="1" placeholder="Capacity" value={roomForm.capacity} onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value })} />
              <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({ ...roomForm, room_type: v })}>
                <SelectTrigger data-testid="room-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="lab">Lab</SelectItem>
                  <SelectItem value="auditorium">Auditorium</SelectItem>
                  <SelectItem value="sports">Sports</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="room-submit-btn" className="col-span-3 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                <Plus size={15} className="mr-1.5" /> Add room
              </Button>
            </form>
            <div className="space-y-2">
              {rooms.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-[#F4F5F7] rounded-xl px-4 py-2.5 text-sm" data-testid={`room-row-${r.id}`}>
                  <span className="font-semibold">{r.room_name} <span className="text-xs font-normal text-slate-400">cap {r.capacity} · {r.room_type}</span></span>
                  <button data-testid={`delete-room-${r.id}`} onClick={() => deleteRoom(r.id)} className="p-1.5 text-slate-400 hover:text-status-red transition-colors duration-200"><Trash size={14} /></button>
                </div>
              ))}
              {rooms.length === 0 && <p className="text-sm text-slate-400">No rooms yet.</p>}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-heading font-bold mb-4">Subjects</h2>
            <form onSubmit={addSubject} className="grid grid-cols-2 gap-3 mb-4" data-testid="subject-form">
              <Input data-testid="subject-name-input" required placeholder="Subject name" value={subjectForm.subject_name} onChange={(e) => setSubjectForm({ ...subjectForm, subject_name: e.target.value })} />
              <Input data-testid="subject-periods-input" type="number" min="1" max="35" placeholder="Periods/week" value={subjectForm.weekly_required_periods} onChange={(e) => setSubjectForm({ ...subjectForm, weekly_required_periods: e.target.value })} />
              <Button type="submit" data-testid="subject-submit-btn" className="col-span-2 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                <Plus size={15} className="mr-1.5" /> Add subject
              </Button>
            </form>
            <div className="space-y-2">
              {subjects.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-[#F4F5F7] rounded-xl px-4 py-2.5 text-sm" data-testid={`subject-row-${s.id}`}>
                  <span className="font-semibold">{s.subject_name} <span className="text-xs font-normal text-slate-400">{s.weekly_required_periods} periods/week</span></span>
                  <button data-testid={`delete-subject-${s.id}`} onClick={() => deleteSubject(s.id)} className="p-1.5 text-slate-400 hover:text-status-red transition-colors duration-200"><Trash size={14} /></button>
                </div>
              ))}
              {subjects.length === 0 && <p className="text-sm text-slate-400">No subjects yet.</p>}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="breaks-card">
            <h2 className="font-heading font-bold mb-1">Breaks</h2>
            <p className="text-xs text-slate-400 mb-4">Breaks render between periods on every timetable grid (admin, teacher and parent views).</p>
            <form onSubmit={addBreak} className="grid grid-cols-2 gap-3 mb-4" data-testid="break-form">
              <Input data-testid="break-name-input" required placeholder="e.g. Short Break" value={breakForm.name} onChange={(e) => setBreakForm({ ...breakForm, name: e.target.value })} />
              <Select value={breakForm.after_period} onValueChange={(v) => setBreakForm({ ...breakForm, after_period: v })}>
                <SelectTrigger data-testid="break-after-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.slice(0, -1).map((p) => (
                    <SelectItem key={p} value={String(p)}>After P{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="break-submit-btn" className="col-span-2 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                <Plus size={15} className="mr-1.5" /> Add break
              </Button>
            </form>
            <div className="space-y-2">
              {breaks.map((b, i) => (
                <div key={`${b.name}-${i}`} className="flex items-center justify-between bg-[#F4F5F7] rounded-xl px-4 py-2.5 text-sm" data-testid={`break-row-${i}`}>
                  <span className="font-semibold flex items-center gap-2"><Coffee size={14} weight="duotone" className="text-amber-500" /> {b.name} <span className="text-xs font-normal text-slate-400">after P{b.after_period}</span></span>
                  <button data-testid={`delete-break-${i}`} onClick={() => removeBreak(i)} className="p-1.5 text-slate-400 hover:text-status-red transition-colors duration-200"><Trash size={14} /></button>
                </div>
              ))}
              {breaks.length === 0 && <p className="text-sm text-slate-400">No breaks configured.</p>}
            </div>
          </div>
        </div>
      )}

      {view === "constraints" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
              <tr><th className="px-5 py-3">Teacher</th><th className="px-5 py-3">Department</th><th className="px-5 py-3">Max hrs/week</th><th className="px-5 py-3">Unavailable slots</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody>
              {teachers.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`constraint-row-${t.id}`}>
                  <td className="px-5 py-3.5 font-semibold">{t.name}</td>
                  <td className="px-5 py-3.5 text-slate-600">{t.department || "—"}</td>
                  <td className="px-5 py-3.5">{t.max_weekly_hours ?? "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600">{(t.unavailable_slots || []).length} marked</td>
                  <td className="px-5 py-3.5 text-right">
                    <Button variant="outline" size="sm" data-testid={`constraint-edit-${t.id}`} onClick={() => setEditingTeacher(t)} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
                      <PencilSimple size={14} className="mr-1" /> Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "conflicts" && (
        <div data-testid="conflicts-panel">
          {conflicts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
              <CheckCircle size={40} weight="duotone" className="text-status-green mx-auto" />
              <p className="font-heading font-bold mt-3">No scheduling conflicts</p>
              <p className="text-sm text-slate-500 mt-1">All subjects are assigned and nothing is double-booked.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {conflicts.map((c, i) => (
                <div key={i} className="bg-white border border-status-amber/40 rounded-2xl p-4 flex items-center gap-3" data-testid={`conflict-${c.type}-${i}`}>
                  <WarningOctagon size={20} weight="duotone" className={c.type.includes("double") ? "text-status-red shrink-0" : "text-amber-500 shrink-0"} />
                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">{c.type.replace(/_/g, " ")}</span>
                    <p className="text-sm font-semibold">{c.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modal && (
        <SlotModal
          modal={modal}
          classes={classes}
          teachers={teachers}
          rooms={rooms}
          subjects={subjects}
          defaultClassId={filter.class_id}
          onClose={() => setModal(null)}
          onSaved={reloadAll}
        />
      )}
      {editingTeacher && (
        <ConstraintEditor teacher={editingTeacher} onClose={() => setEditingTeacher(null)} onSaved={loadMeta} />
      )}
      <AlertDialog open={confirmClear} onOpenChange={(o) => !o && setConfirmClear(false)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{filter.class_id ? "Clear this class's timetable?" : "Clear the entire timetable?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {filter.class_id
                ? `All scheduled slots for ${classes.find((c) => c.id === filter.class_id)?.name || "this class"} will be removed. Rooms, subjects, breaks and teacher constraints are kept.`
                : "Every scheduled slot across all classes will be removed. Rooms, subjects, breaks and teacher constraints are kept — you can rebuild right away with Auto-generate or by clicking cells."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-timetable-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="clear-timetable-confirm" onClick={clearTimetable} className="bg-status-red hover:bg-status-red/90 text-white">Clear timetable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function TeacherTimetableTab() {
  const { t } = useTranslation();
  const dayLabels = [
    t("timetable.days.monday", { defaultValue: "Monday" }),
    t("timetable.days.tuesday", { defaultValue: "Tuesday" }),
    t("timetable.days.wednesday", { defaultValue: "Wednesday" }),
    t("timetable.days.thursday", { defaultValue: "Thursday" }),
    t("timetable.days.friday", { defaultValue: "Friday" }),
  ];
  const [slots, setSlots] = useState(null);
  const [breaks, setBreaks] = useState([]);

  useEffect(() => {
    api.get("/teacher/timetable").then((r) => setSlots(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/timetable/breaks").then((r) => setBreaks(r.data)).catch(() => {});
  }, []);

  const downloadMyPdf = async () => {
    try {
      const res = await api.get("/teacher/timetable/export", { params: { format: "pdf" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my_timetable.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, t("teacherTimetable.exportFailed", { defaultValue: "Export failed" })));
    }
  };

  if (!slots) {
    return <div className="py-16 grid place-items-center"><div className="w-8 h-8 rounded-full border-4 border-brand-coral border-t-transparent animate-spin" /></div>;
  }
  const todayIdx = (new Date().getDay() + 6) % 7;
  const todaySlots = slots.filter((s) => s.day_of_week === todayIdx);

  return (
    <div data-testid="teacher-timetable-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="font-heading text-2xl font-extrabold">{t("teacherTimetable.heading", { defaultValue: "My timetable" })}</h1>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="teacher-timetable-print" onClick={() => window.open("/print/timetable", "_blank")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <Printer size={15} className="mr-1.5" /> {t("teacherTimetable.print", { defaultValue: "Print" })}
          </Button>
          <Button variant="outline" data-testid="teacher-timetable-pdf" onClick={downloadMyPdf} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <FilePdf size={15} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-6">{t("teacherTimetable.description", { defaultValue: "Read-only weekly schedule. Contact the school admin for changes." })}</p>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
        <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider mb-3">{t("teacherTimetable.today", { defaultValue: "Today" })} · {dayLabels[todayIdx] || t("teacherTimetable.weekend", { defaultValue: "Weekend" })}</h2>
        {todaySlots.length === 0 ? (
          <p className="text-sm text-slate-400">{t("teacherTimetable.noPeriodsToday", { defaultValue: "No periods scheduled today." })}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {todaySlots.map((s) => (
              <span key={s.id} className="bg-brand-coral/10 rounded-xl px-3 py-2 text-xs font-bold text-brand-coralDark" data-testid={`today-slot-${s.id}`}>
                P{s.period_number} · {s.subject_name} · {s.class_name} · {s.room_name}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <WeekGrid slots={slots} breaks={breaks} cellLabel={(s) => `${s.class_name} · ${s.room_name}`} testidPrefix="teacher-cell" />
      </div>
    </div>
  );
}
export function ParentSchedule({ child }) {
  const { t } = useTranslation();
  const [slots, setSlots] = useState(null);
  const [breaks, setBreaks] = useState([]);

  useEffect(() => {
    if (!child) return;
    setSlots(null);
    api.get("/parent/timetable", { params: { student_id: child.id } })
      .then((r) => setSlots(r.data))
      .catch(() => setSlots([]));
    api.get("/timetable/breaks").then((r) => setBreaks(r.data)).catch(() => {});
  }, [child]);

  if (!slots) {
    return (
      <div className="py-16 grid place-items-center">
        <div className="w-8 h-8 rounded-full border-4 border-brand-coral border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="parent-schedule">
      <p className="text-sm text-slate-500 mb-4">
        {t("parentSchedule.weeklyScheduleFor")}{" "}
        <span className="font-bold text-slate-700">{child.name}</span>{" "}
        ({child.class_name})
      </p>

      {slots.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <p className="text-sm text-slate-400">
            {t("parentSchedule.noTimetable")}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-3 overflow-x-auto">
          <WeekGrid
            slots={slots}
            breaks={breaks}
            cellLabel={(s) => s.teacher_name}
            testidPrefix="parent-cell"
          />
        </div>
      )}
    </div>
  );
}
